import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAstrologerSignupRequests,
  approveAstrologerSignup,
} from "../../services/astrologerRequestService";
import { toast } from "react-toastify";

const initialState = {
  requests: [],
  status: "idle",
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
};

/**
 * ===============================
 * GET: Fetch Signup Requests
 * ===============================
 */
export const fetchAstrologerSignupRequests = createAsyncThunk(
  "astrologerSignup/fetchRequests",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getAstrologerSignupRequests(params);
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch signup requests";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * ===============================
 * PUT: Approve / Reject Signup
 * ===============================
 */
export const approveSignupRequest = createAsyncThunk(
  "astrologerSignup/approveSignup",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await approveAstrologerSignup(payload);
      toast.success(response.message || "Signup status updated successfully!");
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to update signup status";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const astrologerSignupSlice = createSlice({
  name: "astrologerSignup",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSignupState: (state) => {
      state.requests = [];
      state.status = "idle";
      state.error = null;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
      };
    },
    setSignupRequests: (state, action) => {
      state.requests = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      /**
       * ===============================
       * FETCH REQUESTS
       * ===============================
       */
      .addCase(fetchAstrologerSignupRequests.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAstrologerSignupRequests.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.requests = action.payload.data || [];

        state.pagination = action.payload.pagination || {
          total: state.requests.length,
          page: 1,
          limit: state.requests.length,
          totalPages: 1,
        };

        state.error = null;
      })
      .addCase(fetchAstrologerSignupRequests.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.requests = [];
        state.pagination = {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
        };
      })

      /**
       * ===============================
       * APPROVE / REJECT REQUEST
       * ===============================
       */
      .addCase(approveSignupRequest.pending, (state) => {
        // keep list usable while approving
      })
      .addCase(approveSignupRequest.fulfilled, (state, action) => {
        const updatedRequest = action.payload;

        if (updatedRequest && Array.isArray(state.requests)) {
          const index = state.requests.findIndex(
            (req) => req._id === updatedRequest._id
          );
          if (index !== -1) {
            state.requests[index] = updatedRequest;
          }
        }
      })
      .addCase(approveSignupRequest.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  resetSignupState,
  setSignupRequests,
} = astrologerSignupSlice.actions;

/**
 * ===============================
 * SELECTORS
 * ===============================
 */
export const selectAllSignupRequests = (state) =>
  state.astrologerSignup.requests;

export const selectSignupRequestById = (state, id) =>
  state.astrologerSignup.requests.find((req) => req._id === id);

export const selectSignupStatus = (state) =>
  state.astrologerSignup.status;

export const selectSignupError = (state) =>
  state.astrologerSignup.error;

export const selectSignupPagination = (state) =>
  state.astrologerSignup.pagination;

export const selectPendingSignupRequests = (state) =>
  state.astrologerSignup.requests.filter(
    (req) => req.status === "pending"
  );

export default astrologerSignupSlice.reducer;
